import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerFinancialCapacityApi } from '../../api/partner-financial-safeguarding-policies';
import { ApiError } from '../../api/client';
import { usePermission } from '../../auth/AuthContext';
import { useToast } from '../../components/Toast';

interface FormValues {
  fundingSources: string; accountingSystem: string; hasFinanceProcedures: boolean; hasSegregationOfDuties: boolean;
  hasBudgetingProcess: boolean; hasBankReconciliation: boolean; hasFinancialReporting: boolean; hasExternalAudit: boolean;
  lastAuditDate: string; hasAssetManagement: boolean; hasFraudControls: boolean; notes: string;
}

/** Restricted (spec section 13) - genuinely hidden client-side for anyone without financial_capacity.view, on top of real server-side enforcement. */
export function PartnerFinancialCapacityTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('partner.due_diligence.financial_capacity.manage');
  const dataQuery = useQuery({ queryKey: ['partner-financial-capacity', partnerId], queryFn: () => partnerFinancialCapacityApi.get(partnerId) });
  const { register, handleSubmit, reset } = useForm<FormValues>();

  useEffect(() => {
    if (dataQuery.data?.data) {
      const d = dataQuery.data.data;
      reset({
        fundingSources: d.funding_sources ?? '', accountingSystem: d.accounting_system ?? '', hasFinanceProcedures: !!d.has_finance_procedures,
        hasSegregationOfDuties: !!d.has_segregation_of_duties, hasBudgetingProcess: !!d.has_budgeting_process, hasBankReconciliation: !!d.has_bank_reconciliation,
        hasFinancialReporting: !!d.has_financial_reporting, hasExternalAudit: !!d.has_external_audit, lastAuditDate: d.last_audit_date ?? '',
        hasAssetManagement: !!d.has_asset_management, hasFraudControls: !!d.has_fraud_controls, notes: d.notes ?? '',
      });
    }
  }, [dataQuery.data, reset]);

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) => partnerFinancialCapacityApi.upsert(partnerId, values),
    onSuccess: () => { showSuccess('Financial capacity assessment saved.'); queryClient.invalidateQueries({ queryKey: ['partner-financial-capacity', partnerId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  if (!canManage && !dataQuery.data) return <p className="muted">Loading…</p>;

  return (
    <div className="panel">
      <div className="panel-body">
        <form className="inline-form" onSubmit={handleSubmit((values) => saveMutation.mutate(values))} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <label style={{ width: '100%' }}>Funding Sources<textarea {...register('fundingSources')} disabled={!canManage} /></label>
          <label style={{ width: '100%' }}>Accounting System<input {...register('accountingSystem')} disabled={!canManage} /></label>
          <label><input type="checkbox" {...register('hasFinanceProcedures')} disabled={!canManage} /> Has Finance Procedures</label>
          <label><input type="checkbox" {...register('hasSegregationOfDuties')} disabled={!canManage} /> Has Segregation of Duties</label>
          <label><input type="checkbox" {...register('hasBudgetingProcess')} disabled={!canManage} /> Has Budgeting Process</label>
          <label><input type="checkbox" {...register('hasBankReconciliation')} disabled={!canManage} /> Has Bank Reconciliation</label>
          <label><input type="checkbox" {...register('hasFinancialReporting')} disabled={!canManage} /> Has Financial Reporting</label>
          <label><input type="checkbox" {...register('hasExternalAudit')} disabled={!canManage} /> Has External Audit</label>
          <label>Last Audit Date<input type="date" {...register('lastAuditDate')} disabled={!canManage} /></label>
          <label><input type="checkbox" {...register('hasAssetManagement')} disabled={!canManage} /> Has Asset Management</label>
          <label><input type="checkbox" {...register('hasFraudControls')} disabled={!canManage} /> Has Fraud Controls</label>
          <label style={{ width: '100%' }}>Notes<textarea {...register('notes')} disabled={!canManage} /></label>
          {canManage && <button type="submit" disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : 'Save Assessment'}</button>}
        </form>
      </div>
    </div>
  );
}
